import React from 'react';
import styled from 'styled-components';

const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  background-color: #1A1A1A;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  
  @media (max-width: 768px) {
    height: 300px;
    padding: 15px;
  }
`;

const ChartTitle = styled.h3`
  color: #FFFFFF;
  font-size: 18px;
  margin-bottom: 16px;
  text-align: center;
`;

const ChartArea = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  background-color: #121212;
  border-radius: 8px;
  padding: 20px;
  overflow: hidden;
`;

const GridLines = styled.div`
  position: absolute;
  top: 20px;
  left: 60px;
  right: 20px;
  bottom: 60px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(to right, #333 1px, transparent 1px),
      linear-gradient(to bottom, #333 1px, transparent 1px);
    background-size: 50px 40px;
  }
`;

const ChartLine = styled.svg`
  position: absolute;
  top: 20px;
  left: 60px;
  right: 20px;
  bottom: 60px;
  width: calc(100% - 80px);
  height: calc(100% - 80px);
`;

const DataPoint = styled.circle`
  fill: ${props => props.isCurrent ? '#FFD700' : props.isFuture ? '#FF9800' : '#2196F3'};
  stroke: ${props => props.isCurrent ? '#FFD700' : props.isFuture ? '#FF9800' : '#2196F3'};
  stroke-width: 2;
`;

const YAxisLabels = styled.div`
  position: absolute;
  left: 0;
  top: 20px;
  bottom: 60px;
  width: 50px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  padding-right: 10px;
  font-size: 12px;
  color: #888;
`;

const XAxisLabels = styled.div`
  position: absolute;
  bottom: 0;
  left: 60px;
  right: 20px;
  height: 50px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-top: 10px;
  font-size: 12px;
  color: #888;
`;

const CurrentMarker = styled.div`
  position: absolute;
  top: 20px;
  bottom: 60px;
  width: 2px;
  background-color: #FFD700;
  opacity: 0.7;
  
  &::after {
    content: 'Current Year';
    position: absolute;
    top: -20px;
    left: 5px;
    font-size: 10px;
    color: #FFD700;
    white-space: nowrap;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  background-color: #2A2A2A;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 8px 12px;
  color: #FFFFFF;
  font-size: 12px;
  pointer-events: none;
  z-index: 10;
  transform: translate(-50%, -100%);
  margin-top: -10px;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #444;
  }
`;

const SimpleDepreciationChart = ({ depreciationData }) => {
  const [hoveredPoint, setHoveredPoint] = React.useState(null);

  if (!depreciationData || depreciationData.length === 0) return null;

  const currentYear = new Date().getFullYear();
  const minValue = Math.min(...depreciationData.map(d => d.value));
  const maxValue = Math.max(...depreciationData.map(d => d.value));
  const valueRange = maxValue - minValue;
  
  const chartWidth = 100; // percentage
  const chartHeight = 100; // percentage
  
  const getPointPosition = (item, index) => {
    const x = (index / (depreciationData.length - 1)) * chartWidth;
    const y = chartHeight - ((item.value - minValue) / valueRange) * chartHeight;
    return { x, y };
  };

  const pathData = depreciationData.map((item, index) => {
    const pos = getPointPosition(item, index);
    return `${index === 0 ? 'M' : 'L'} ${pos.x} ${pos.y}`;
  }).join(' ');

  const yAxisLabels = [];
  for (let i = 0; i <= 5; i++) {
    const value = minValue + (valueRange / 5) * i;
    yAxisLabels.push(`$${Math.round(value / 1000)}k`);
  }

  const xAxisLabels = depreciationData.filter((_, index) => index % Math.ceil(depreciationData.length / 6) === 0);

  const currentYearIndex = depreciationData.findIndex(d => d.year === currentYear);
  const currentMarkerPosition = currentYearIndex >= 0 ? (currentYearIndex / (depreciationData.length - 1)) * 100 : null;

  return (
    <ChartContainer>
      <ChartTitle>Value Depreciation Timeline</ChartTitle>
      <ChartArea>
        <GridLines />
        
        {currentMarkerPosition && (
          <CurrentMarker style={{ left: `${60 + (currentMarkerPosition * (100 - 80)) / 100}px` }} />
        )}
        
        <YAxisLabels>
          {yAxisLabels.reverse().map((label, index) => (
            <div key={index}>{label}</div>
          ))}
        </YAxisLabels>
        
        <XAxisLabels>
          {xAxisLabels.map((item, index) => (
            <div key={index}>{item.year}</div>
          ))}
        </XAxisLabels>
        
        <ChartLine viewBox="0 0 100 100" preserveAspectRatio="none">
          <path
            d={pathData}
            fill="none"
            stroke="#2196F3"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
          
          {depreciationData.map((item, index) => {
            const pos = getPointPosition(item, index);
            return (
              <DataPoint
                key={index}
                cx={pos.x}
                cy={pos.y}
                r="1"
                isCurrent={item.isCurrent}
                isFuture={item.isFuture}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredPoint({ item, index, pos })}
                onMouseLeave={() => setHoveredPoint(null)}
              />
            );
          })}
        </ChartLine>
        
        {hoveredPoint && (
          <Tooltip
            style={{
              left: `${60 + (hoveredPoint.pos.x * (100 - 80)) / 100}px`,
              top: `${20 + (hoveredPoint.pos.y * (100 - 80)) / 100}px`
            }}
          >
            <div><strong>Year:</strong> {hoveredPoint.item.year}</div>
            <div><strong>Value:</strong> ${hoveredPoint.item.value.toLocaleString()}</div>
            <div><strong>Age:</strong> {hoveredPoint.item.age} years</div>
            {hoveredPoint.item.isCurrent && <div style={{ color: '#FFD700' }}>📍 Current Value</div>}
            {hoveredPoint.item.isFuture && <div style={{ color: '#FF9800' }}>📈 Projected</div>}
          </Tooltip>
        )}
      </ChartArea>
    </ChartContainer>
  );
};

export default SimpleDepreciationChart;